import { lazy, Suspense } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Transaction, Conta, Category } from "@/types/database";
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval, startOfYear, endOfYear, eachDayOfInterval, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useMemo } from 'react';
import { LazyChart } from './LazyChart';
import { AnnualBalanceChart, ExpenseCategoryChart, IncomeCategoryChart } from './ChartComponents';
import { useIsMobile } from "@/hooks/use-mobile";
import { useDataRefresh } from "@/contexts/DataRefreshContext";
import { getEffectiveTransactionDate } from '@/lib/utils';
import { CustomTooltipProps, TooltipPayload } from "@/types/recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart3 } from 'lucide-react';
import { EmptyState } from '../ui/EmptyState';

// Componentes lazy-loaded para melhorar performance
const LazyPieChart = lazy(() => import('recharts').then(module => ({ default: module.PieChart })));
const LazyBarChart = lazy(() => import('recharts').then(module => ({ default: module.BarChart })));
const LazyLineChart = lazy(() => import('recharts').then(module => ({ default: module.LineChart })));
const LazyAreaChart = lazy(() => import('recharts').then(module => ({ default: module.AreaChart })));

interface TransactionWithCategory extends Transaction {
  categoria?: Category;
}

import { useDashboardData } from '@/hooks/useDashboardData';

interface DashboardChartsProps {
  transactions: Transaction[];
  accounts?: Conta[];
  selectedDate?: Date;
  onViewMore?: (chartType: string) => void;
  onNavigateToTransactions?: (month: number, year: number) => void;
}

export function DashboardCharts({ transactions, accounts = [], selectedDate, onViewMore, onNavigateToTransactions }: DashboardChartsProps) {
  type BarClickData = { payload?: { month?: string } };
  const isMobile = useIsMobile();
  const { refreshTrigger } = useDataRefresh();

  // Usar o hook personalizado para gerenciar dados do dashboard
  const { formatCurrency } = useDashboardData({ transactions, accounts, selectedDate });

  // Cores memoizadas para evitar recriação a cada renderização
  const colors = useMemo(() => [
    'hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))',
    'hsl(var(--primary))', 'hsl(var(--secondary))',
    'hsl(var(--income))', 'hsl(var(--expense))', 'hsl(var(--balance))'
  ], []);

  // Filtrar transações com memoização para evitar recálculos desnecessários
  const filteredTransactions = useMemo(() => {
    // Considerar TODAS as transações, independente do status de pagamento
    return selectedDate ? transactions.filter(t => {
      const transactionDate = getEffectiveTransactionDate(t, accounts);
      if (!(transactionDate instanceof Date) || isNaN(transactionDate.getTime())) return false;
      const selectedMonth = selectedDate.getMonth();
      const selectedYear = selectedDate.getFullYear();

      return transactionDate.getMonth() === selectedMonth && transactionDate.getFullYear() === selectedYear && !t.ignorar_transacao;
    }) : transactions.filter(t => {
      const transactionDate = getEffectiveTransactionDate(t, accounts);
      if (!(transactionDate instanceof Date) || isNaN(transactionDate.getTime())) return false;

      return !t.ignorar_transacao;
    });
  }, [selectedDate, transactions, accounts]);

  // Processar dados com useMemo para forçar recalculação quando refreshTrigger mudar
  const chartData = useMemo(() => {
    // Usando as transações já filtradas anteriormente
    const expenseTransactions = filteredTransactions.filter(t => t.tipo === 'saida');

    const incomeTransactions = filteredTransactions.filter(t => t.tipo === 'entrada');

    // Dados para despesas por categoria
    const expensesByCategory = expenseTransactions
      .reduce((acc, transaction) => {
        const categoryName = (transaction as TransactionWithCategory).categoria?.nome || 'Sem categoria';
        if (!acc[categoryName]) {
          acc[categoryName] = 0;
        }
        acc[categoryName] += transaction.valor;
        return acc;
      }, {} as Record<string, number>);

    const expenseCategoryData = Object.entries(expensesByCategory).map(([name, value], index) => ({
      name,
      value,
      color: colors[index % colors.length]
    }));

    const totalExpenses = Object.values(expensesByCategory).reduce((sum, value) => sum + value, 0);

    // Dados para receitas por categoria
    const incomeByCategory = incomeTransactions
      .reduce((acc, transaction) => {
        const categoryName = (transaction as TransactionWithCategory).categoria?.nome || 'Sem categoria';
        if (!acc[categoryName]) {
          acc[categoryName] = 0;
        }
        acc[categoryName] += transaction.valor;
        return acc;
      }, {} as Record<string, number>);

    const incomeCategoryData = Object.entries(incomeByCategory).map(([name, value], index) => ({
      name,
      value,
      color: colors[index % colors.length]
    }));

    const totalIncome = Object.values(incomeByCategory).reduce((sum, value) => sum + value, 0);

    // Frequência de gastos (mês selecionado)
    const baseForFrequency = selectedDate ? new Date(selectedDate) : new Date();
    const freqMonthStart = startOfMonth(baseForFrequency);
    const freqMonthEnd = endOfMonth(baseForFrequency);

    const daysInSelectedMonth = eachDayOfInterval({ start: freqMonthStart, end: freqMonthEnd });

    const spendingFrequencyData = daysInSelectedMonth.map(date => {
      const dayTransactions = transactions.filter(t => {
        const transactionDate = getEffectiveTransactionDate(t, accounts);
        if (!(transactionDate instanceof Date) || isNaN(transactionDate.getTime())) return false;

        return (
          transactionDate.getFullYear() === date.getFullYear() &&
          transactionDate.getMonth() === date.getMonth() &&
          transactionDate.getDate() === date.getDate() &&
          t.tipo === 'saida' &&
          !t.ignorar_transacao
        );
      });

      return {
        day: format(date, 'dd/MM', { locale: ptBR }),
        amount: dayTransactions.reduce((sum, t) => sum + t.valor, 0),
        count: dayTransactions.length
      };
    });

    // Intervalo de últimos 6 meses (mantido para outros gráficos)
    const sixMonthsAgo = subMonths(new Date(), 6);
    const monthsLast6 = eachMonthOfInterval({
      start: sixMonthsAgo,
      end: new Date()
    });

    // Balanço anual (todo o ano da data selecionada ou ano atual)
    const targetYear = selectedDate ? selectedDate.getFullYear() : new Date().getFullYear();
    const yearStart = startOfYear(new Date(targetYear, 0, 1));
    const yearEnd = endOfYear(new Date(targetYear, 0, 1));
    const yearMonths = eachMonthOfInterval({
      start: yearStart,
      end: yearEnd
    });

    const yearTransactions = transactions.filter(t => {
      const transactionDate = getEffectiveTransactionDate(t, accounts);
      if (!(transactionDate instanceof Date) || isNaN(transactionDate.getTime())) return false;

      // Considerar apenas transações do ano alvo e não ignoradas
      return transactionDate.getFullYear() === targetYear && !t.ignorar_transacao;
    });

    const monthlyBalanceData = yearMonths.map(month => {
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);

      const monthTransactions = yearTransactions.filter(t => {
        const transactionDate = getEffectiveTransactionDate(t, accounts);
        if (!(transactionDate instanceof Date) || isNaN(transactionDate.getTime())) return false;
        // Usar comparação de mês e ano para maior robustez
        return transactionDate.getMonth() === month.getMonth() && transactionDate.getFullYear() === month.getFullYear();
      });

      const income = monthTransactions
        .filter(t => t.tipo === 'entrada')
        .reduce((sum, t) => sum + t.valor, 0);

      const expenses = monthTransactions
        .filter(t => t.tipo === 'saida')
        .reduce((sum, t) => sum + t.valor, 0);

      return {
        month: format(month, 'MMM', { locale: ptBR }),
        balance: income - expenses,
        income,
        expenses
      };
    });

    // Balanço do mês selecionado (ou mês atual)
    const selectedBaseMonth = selectedDate ? new Date(selectedDate) : new Date();
    const selectedMonthStart = startOfMonth(selectedBaseMonth);
    const selectedMonthEnd = endOfMonth(selectedBaseMonth);

    const selectedMonthTransactions = transactions.filter(t => {
      const transactionDate = getEffectiveTransactionDate(t, accounts);
      if (!(transactionDate instanceof Date) || isNaN(transactionDate.getTime())) return false;
      return transactionDate >= selectedMonthStart && transactionDate <= selectedMonthEnd && !t.ignorar_transacao;
    });

    const selectedMonthIncome = selectedMonthTransactions
      .filter(t => t.tipo === 'entrada')
      .reduce((sum, t) => sum + t.valor, 0);

    const selectedMonthExpenses = selectedMonthTransactions
      .filter(t => t.tipo === 'saida')
      .reduce((sum, t) => sum + t.valor, 0);

    const selectedMonthBalance = selectedMonthIncome - selectedMonthExpenses;

    const monthlyBalanceSummaryData = [
      { name: 'Receitas', value: selectedMonthIncome, color: '#10b981' },
      { name: 'Despesas', value: selectedMonthExpenses, color: '#ef4444' },
      { name: 'Balanço', value: selectedMonthBalance, color: selectedMonthBalance >= 0 ? '#10b981' : '#ef4444' }
    ];

    // Despesas pendentes do mês selecionado
    const baseDateForPending = selectedDate ? new Date(selectedDate) : new Date();
    const pm = baseDateForPending.getMonth();
    const py = baseDateForPending.getFullYear();
    const pendingSumThisMonth = (transactions || [])
      .filter(t => t.tipo === 'saida' && !t.foi_paga && !(String(t.id || '').startsWith('virtual-')) && !t.ignorar_transacao)
      .filter(t => {
        const d = getEffectiveTransactionDate(t, accounts);
        if (!(d instanceof Date) || isNaN(d.getTime())) return false;
        return d.getMonth() === pm && d.getFullYear() === py;
      })
      .reduce((sum, t) => sum + Number(t.valor || 0), 0);

    // Receitas x Despesas (ano inteiro)
    const revenueVsExpensesData = yearMonths.map(month => {
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);

      const monthTransactions = yearTransactions.filter(t => {
        const transactionDate = getEffectiveTransactionDate(t, accounts);
        if (!(transactionDate instanceof Date) || isNaN(transactionDate.getTime())) return false;
        // Usar comparação de mês e ano para maior robustez
        return transactionDate.getMonth() === month.getMonth() && transactionDate.getFullYear() === month.getFullYear();
      });

      const income = monthTransactions
        .filter(t => t.tipo === 'entrada')
        .reduce((sum, t) => sum + t.valor, 0);

      const expenses = monthTransactions
        .filter(t => t.tipo === 'saida')
        .reduce((sum, t) => sum + t.valor, 0);

      return {
        month: format(month, 'MMM', { locale: ptBR }),
        receitas: income,
        despesas: expenses
      };
    });

    // Receitas diárias do mês selecionado
    const daysInMonth = eachDayOfInterval({
      start: selectedMonthStart,
      end: selectedMonthEnd
    });

    const dailyIncomeData = daysInMonth.map(day => {
      const dayStart = startOfDay(day);
      const dayEnd = endOfDay(day);

      const dayTransactions = selectedMonthTransactions.filter(t => {
        const transactionDate = getEffectiveTransactionDate(t, accounts);
        if (!(transactionDate instanceof Date) || isNaN(transactionDate.getTime())) return false;
        return transactionDate >= dayStart && transactionDate <= dayEnd;
      });

      const income = dayTransactions
        .filter(t => t.tipo === 'entrada')
        .reduce((sum, t) => sum + t.valor, 0);

      return {
        day: format(day, 'dd', { locale: ptBR }),
        income
      };
    });



    return {
      expenseCategoryData,
      totalExpenses,
      incomeCategoryData,
      totalIncome,
      spendingFrequencyData,
      monthlyBalanceData,
      selectedMonthIncome,
      selectedMonthExpenses,
      selectedMonthBalance,
      monthlyBalanceSummaryData,
      pendingSumThisMonth,
      revenueVsExpensesData,
      dailyIncomeData
    };
  }, [transactions, selectedDate, accounts, colors, filteredTransactions]);

  // Evolução do patrimônio (últimos 12 meses)
  const netWorthEvolutionData = useMemo(() => {
    // Garantir que usamos o final do mês selecionado como referência
    const baseDate = selectedDate ? new Date(selectedDate) : new Date();
    const endDate = endOfMonth(baseDate);
    // 11 meses atrás a partir do início do mês final
    const startDate = startOfMonth(subMonths(endDate, 11));
    const monthsPeriod = eachMonthOfInterval({ start: startDate, end: endDate });

    const validTransactions = (transactions || []).filter(t => {
      const d = getEffectiveTransactionDate(t, accounts);
      if (!(d instanceof Date) || isNaN(d.getTime())) return false;
      return !t.ignorar_transacao;
    });

    const initialTransactions = validTransactions.filter(t => {
      const d = getEffectiveTransactionDate(t, accounts);
      return d < startDate;
    });

    const initialIncome = initialTransactions
      .filter(t => t.tipo === 'entrada')
      .reduce((sum, t) => sum + Number(t.valor || 0), 0);

    const initialExpenses = initialTransactions
      .filter(t => t.tipo === 'saida')
      .reduce((sum, t) => sum + Number(t.valor || 0), 0);

    let patrimonioAcumulado = initialIncome - initialExpenses;

    return monthsPeriod.map(month => {
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);

      const monthTransactions = validTransactions.filter(t => {
        const d = getEffectiveTransactionDate(t, accounts);
        // Usar comparação de mês e ano para evitar problemas com fuso horário ou limites de data
        return d.getMonth() === month.getMonth() && d.getFullYear() === month.getFullYear();
      });

      const receitas = monthTransactions
        .filter(t => t.tipo === 'entrada')
        .reduce((sum, t) => sum + Number(t.valor || 0), 0);

      const despesas = monthTransactions
        .filter(t => t.tipo === 'saida')
        .reduce((sum, t) => sum + Number(t.valor || 0), 0);

      const saldoMensal = receitas - despesas;
      patrimonioAcumulado += saldoMensal;

      return {
        month: format(month, 'MMM', { locale: ptBR }),
        receitas,
        despesas,
        saldoMensal,
        patrimonioAcumulado
      };
    });
  }, [transactions, selectedDate, accounts]);

  // Extrair dados do useMemo
  const {
    expenseCategoryData,
    totalExpenses,
    incomeCategoryData,
    totalIncome,
    spendingFrequencyData,
    monthlyBalanceData,
    selectedMonthIncome,
    selectedMonthExpenses,
    selectedMonthBalance,
    monthlyBalanceSummaryData,
    pendingSumThisMonth,
    revenueVsExpensesData,
    dailyIncomeData
  } = chartData;

  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (!active || !payload || !payload.length) {
      return <div className="hidden"></div>;
    }

    return (
      <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
        <p className="font-medium mb-2">{label}</p>
        {payload.map((entry: TooltipPayload, index: number) => (
          <p key={index} className="text-sm tabular-nums" style={{ color: entry.color }}>
            {entry.name || entry.dataKey}: {formatCurrency(entry.value as number)}
          </p>
        ))}
      </div>
    );
  };

  const RevenueVsExpensesTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (!active || !payload || !payload.length) {
      return <div className="hidden"></div>;
    }

    const resolveLabel = (entry: TooltipPayload) => {
      const key = entry.dataKey || entry.name || '';
      if (key === 'receitas' || key === 'Receitas') return 'Receitas';
      if (key === 'despesas' || key === 'Despesas') return 'Despesas';
      return key || 'Valor';
    };

    return (
      <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
        <p className="font-medium mb-2">{label}</p>
        {payload.map((entry: TooltipPayload, index: number) => (
          <p key={index} className="text-sm tabular-nums" style={{ color: entry.color }}>
            {resolveLabel(entry)}: {formatCurrency(Number(entry.value || 0))}
          </p>
        ))}
      </div>
    );
  };

  if (transactions.length === 0) {
    return (
      <Card className="shadow-soft border-primary/10">
        <CardContent className="pt-6">
          <EmptyState
            icon={BarChart3}
            title="Dashboard vazio"
            description="Sem transações cadastradas para gerar gráficos. Comece adicionando seus ganhos e gastos!"
          />
        </CardContent>
      </Card>
    );
  }

  // No mobile, mostrar apenas o gráfico de despesas por categoria
  if (isMobile) {
    return (
      <div className="space-y-4" data-testid="dashboard-charts-container">
        <ExpenseCategoryChart
          data={expenseCategoryData}
          total={totalExpenses}
          onViewMore={() => onViewMore?.('expenses-by-category')}
          formatCurrency={formatCurrency}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6" data-testid="dashboard-charts-container">
      <ExpenseCategoryChart
        data={expenseCategoryData}
        total={totalExpenses}
        onViewMore={() => onViewMore?.('expenses-by-category')}
        formatCurrency={formatCurrency}
      />

      <IncomeCategoryChart
        data={incomeCategoryData}
        total={totalIncome}
        onViewMore={() => onViewMore?.('income-by-category')}
        formatCurrency={formatCurrency}
      />

      {/* Frequência de gastos */}
      <Card className="bg-card border hover:border-primary/50 hover:shadow-md transition-all duration-300 cursor-pointer shadow-soft"
        onClick={() => onViewMore?.('spending-frequency')}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-h4">Despesas do mês</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={spendingFrequencyData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="day"
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => {
                  if (typeof v === 'number') return String(v).padStart(2, '0');
                  if (typeof v === 'string') {
                    if (v.includes('/')) {
                      const [day] = v.split('/');
                      return String(day).padStart(2, '0');
                    }
                    if (v.includes('-')) {
                      const parts = v.split('-');
                      const day = parts[2] ?? v;
                      return String(day).padStart(2, '0');
                    }
                    return v;
                  }
                  return String(v);
                }}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => {
                  if (value >= 1000) {
                    return `R$ ${(value / 1000).toFixed(0)}k`;
                  }
                  return `R$ ${value}`;
                }}
                className="tabular-nums"
                domain={['auto', 'auto']}
                allowDataOverflow={false}
              />
              <Tooltip
                content={(props) => {
                  const { active, payload, label } = props;
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    const labelStr = String(label ?? '');
                    const dayOnly = (() => {
                      if (labelStr.includes('/')) {
                        const [day] = labelStr.split('/');
                        return String(day).padStart(2, '0');
                      }
                      if (labelStr.includes('-')) {
                        const parts = labelStr.split('-');
                        const day = parts[2] ?? labelStr;
                        return String(day).padStart(2, '0');
                      }
                      return String(labelStr).padStart(2, '0');
                    })();
                    return (
                      <div className="bg-card border border-border p-3 rounded-md shadow-md">
                        <p className="text-sm font-medium mb-1">Dia {dayOnly}</p>
                        <p className="text-sm flex items-center justify-between gap-2">
                          <span className="text-red-500">Despesas:</span>
                          <span className="font-semibold text-red-500 tabular-nums">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(data.amount ?? 0))}
                          </span>
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="amount"
                stroke="#ef4444"
                fillOpacity={1}
                fill="url(#colorAmount)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Receitas do mês */}
      <Card className="bg-card border hover:border-primary/50 hover:shadow-md transition-all duration-300 cursor-pointer shadow-soft"
        onClick={() => onViewMore?.('monthly-income')}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-h4">Receitas do mês</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {dailyIncomeData.some(item => item.income > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={dailyIncomeData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="day"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  domain={['auto', 'auto']}
                  allowDataOverflow={false}
                  className="tabular-nums"
                  tickFormatter={(value) => {
                    const absValue = Math.abs(value);
                    const sign = value < 0 ? '-' : '';
                    if (absValue >= 1000) {
                      return `${sign}R$ ${(absValue / 1000).toFixed(0)}k`;
                    }
                    return `${sign}R$ ${absValue}`;
                  }}
                />
                <Tooltip
                  formatter={(value) => {
                    const formatted = typeof value === 'number' ? formatCurrency(value) : String(value);
                    return [formatted, 'Receitas'];
                  }}
                  labelFormatter={(label) => `Dia ${label}`}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    padding: '10px',
                    fontSize: '14px'
                  }}
                  itemStyle={{
                    padding: '4px 0',
                    color: '#10b981'
                  }}
                  labelStyle={{
                    fontWeight: 'bold',
                    marginBottom: '6px'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="income"
                  stroke="#10b981"
                  fillOpacity={1}
                  fill="url(#colorIncome)"
                  strokeWidth={2}
                  name="Receitas"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[200px] w-full items-center justify-center text-muted-foreground text-sm">
              Sem receitas neste mês
            </div>
          )}
        </CardContent>
      </Card>

      {/* Balanço Anual */}
      <AnnualBalanceChart
        data={monthlyBalanceData}
        onViewMore={onViewMore}
      />

      {/* Evolução do Patrimônio */}
      <Card className="bg-card border hover:border-primary/50 hover:shadow-md transition-all duration-300 cursor-pointer shadow-soft"
        onClick={() => onViewMore?.('net-worth-evolution')}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-h4">Evolução do Patrimônio</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {netWorthEvolutionData.some(item => item.patrimonioAcumulado !== 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={netWorthEvolutionData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorPatrimonio" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(value) => {
                    const absValue = Math.abs(Number(value));
                    const sign = Number(value) < 0 ? '-' : '';
                    if (absValue >= 1000) {
                      return `${sign}R$ ${(absValue / 1000).toFixed(0)}k`;
                    }
                    return `${sign}R$ ${absValue}`;
                  }}
                  className="tabular-nums"
                />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="patrimonioAcumulado" stroke="hsl(var(--chart-2))" fillOpacity={1} fill="url(#colorPatrimonio)" strokeWidth={2} name="Patrimônio" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[200px] w-full items-center justify-center text-muted-foreground text-sm">
              Sem dados de patrimônio
            </div>
          )}
        </CardContent>
      </Card>

      {/* Receitas x Despesas */}
      <Card className="bg-card border hover:border-primary/50 hover:shadow-md transition-all duration-300 cursor-pointer shadow-soft"
        onClick={() => onViewMore?.('revenue-vs-expenses')}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-h4">Receitas x Despesas</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {revenueVsExpensesData.some(item => item.receitas > 0 || item.despesas > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={revenueVsExpensesData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="month"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  domain={['auto', 'auto']}
                  allowDataOverflow={false}
                  className="tabular-nums"
                  tickFormatter={(value) => {
                    if (value >= 1000) {
                      return `R$ ${(value / 1000).toFixed(0)}k`;
                    }
                    return `R$ ${value}`;
                  }}
                />
                <Tooltip content={<RevenueVsExpensesTooltip />} />
                <Bar dataKey="receitas" fill="#10b981" name="Receitas" />
                <Bar dataKey="despesas" fill="#ef4444" name="Despesas" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[200px] w-full items-center justify-center text-muted-foreground text-sm">
              Sem dados comparativos
            </div>
          )}
        </CardContent>
      </Card>

      {/* Balanço Mensal */}
      <Card className="bg-card border hover:border-primary/50 hover:shadow-md transition-all duration-300 cursor-pointer shadow-soft"
        onClick={() => onViewMore?.('monthly-balance')}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-h4">Balanço Mensal</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            {monthlyBalanceSummaryData.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-body-sm font-medium">{item.name}</span>
                </div>
                <span className="text-xs sm:text-sm font-semibold tabular-nums">{formatCurrency(item.value)}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
